import React, { memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiEdit2, FiTrash2, FiCheck, FiClock, FiPlay, FiMove, FiZap } from 'react-icons/fi';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Task {
  id: string;
  title: string;
  description: string | null;
  category: string;
  impact_level: number;
  impact_type: string;
  phase: string;
  estimated_hours: number;
  time_spent: number;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  assigned_to: string | null;
  assigned_to_email: string | null;
  assigned_to_name: string | null;
  project: string;
  priority_order: number;
}

interface SortableTaskItemProps {
  task: Task;
  index: number;
  onEdit: (task: Task) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (task: Task) => void;
  onFinish: (task: Task) => void;
  getCategoryColor: (category: string) => string;
  getStatusColor: (status: string) => string;
  getImpactColor: (level: number) => string;
}

// Memoized for performance
const SortableTaskItem = memo(function SortableTaskItem({
  task,
  index,
  onEdit,
  onDelete,
  onToggleStatus,
  onFinish,
  getCategoryColor,
  getStatusColor,
  getImpactColor,
}: SortableTaskItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms cubic-bezier(0.22, 1, 0.36, 1)',
    zIndex: isDragging ? 1000 : 1,
  };

  const isCompleted = task.status === 'Completed';
  const isInProgress = task.status === 'In Progress';

  // Progress percentage based on time spent vs estimated
  const progressPercent = task.estimated_hours > 0 
    ? Math.min(100, Math.round((task.time_spent / 60) / task.estimated_hours * 100))
    : 0;

  // Memoized callbacks
  const handleFinish = useCallback(() => onFinish(task), [onFinish, task]);
  const handleEdit = useCallback(() => onEdit(task), [onEdit, task]);
  const handleDelete = useCallback(() => onDelete(task.id), [onDelete, task.id]);
  const handleToggle = useCallback(() => onToggleStatus(task), [onToggleStatus, task]);

  // Format time helper
  const formatTime = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    if (h === 0) return `${m}m`;
    return m > 0 ? `${h}h${m}m` : `${h}h`;
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ 
        opacity: isDragging ? 0.5 : 1, 
        x: 0,
        scale: isDragging ? 1.02 : 1,
        boxShadow: isDragging 
          ? '0 25px 50px -12px rgba(139, 92, 246, 0.25)' 
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ 
        type: 'spring', 
        stiffness: 500, 
        damping: 35,
        mass: 0.8
      }}
      className={`group relative overflow-hidden backdrop-blur-sm border rounded-2xl transition-all duration-300 ${
        isDragging 
          ? 'border-purple-500 bg-gray-900/90 ring-2 ring-purple-500/30' 
          : isInProgress
          ? 'border-cyan-500/40 bg-gradient-to-r from-gray-900/80 to-gray-900/60 hover:border-cyan-400/60'
          : 'border-gray-700/40 bg-gray-900/60 hover:border-gray-600/60 hover:bg-gray-900/80'
      }`}
    >
      {/* Priority number badge */}
      <div className="absolute -left-1 top-4 z-10">
        <div className={`px-3 py-1 rounded-r-full text-xs font-bold shadow-lg ${
          index === 0 
            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' 
            : index < 3 
            ? 'bg-gradient-to-r from-purple-600 to-cyan-500 text-white'
            : 'bg-gray-700/80 text-gray-300'
        }`}>
          #{index + 1}
        </div>
      </div>

      {/* Progress bar for in-progress tasks */}
      {isInProgress && task.estimated_hours > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-800">
          <motion.div 
            className="h-full bg-gradient-to-r from-cyan-500 to-purple-500"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      )}

      <div className="flex items-start gap-3 p-4 pl-10">
        {/* Drag handle */}
        {!isCompleted && (
          <button
            {...attributes}
            {...listeners}
            className="mt-1 p-2.5 rounded-xl bg-gray-800/60 text-gray-400 hover:text-white hover:bg-gray-700/80 cursor-grab active:cursor-grabbing transition-all shrink-0 border border-transparent hover:border-gray-600/50 group-hover:scale-105"
            title="Drag to reorder (or use keyboard)"
          >
            <FiMove className="w-4 h-4" />
          </button>
        )}
        
        <div className="flex-1 min-w-0">
          {/* Tags row */}
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border backdrop-blur-sm ${getCategoryColor(task.category)}`}>
              {task.category}
            </span>
            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusColor(task.status)}`}>
              {isInProgress && <FiZap className="inline w-3 h-3 mr-1" />}
              {task.status}
            </span>
            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${getImpactColor(task.impact_level)}`}>
              ⚡ {task.impact_level}/5
            </span>
            <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-700/60 text-gray-300 border border-gray-600/30">
              {task.impact_type}
            </span>
            {task.assigned_to_name && (
              <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                → {task.assigned_to_name}
              </span>
            )}
          </div>
          
          {/* Title */}
          <h4 className={`font-semibold text-base leading-tight ${
            isCompleted ? 'text-gray-500 line-through' : 'text-white'
          }`}>
            {task.title}
          </h4>
          
          {/* Description */}
          {task.description && (
            <p className="text-gray-400 text-sm mt-1.5 line-clamp-2 leading-relaxed">
              {task.description}
            </p>
          )}
          
          {/* Meta info */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {task.estimated_hours > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <FiClock className="w-3.5 h-3.5" />
                <span>Est: {task.estimated_hours}h</span>
              </div>
            )}
            {task.time_spent > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-cyan-400 font-medium">
                <FiPlay className="w-3.5 h-3.5" />
                <span>{formatTime(task.time_spent)}</span>
                {task.estimated_hours > 0 && (
                  <span className="text-gray-500">({progressPercent}%)</span>
                )}
              </div>
            )}
            <span className="text-xs text-gray-600">
              {new Date(task.created_at).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              })}
            </span>
            {task.completed_at && (
              <span className="text-xs text-green-400 font-medium flex items-center gap-1">
                <FiCheck className="w-3 h-3" />
                {new Date(task.completed_at).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </span>
            )}
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Finish button - prominent for non-completed */}
          {!isCompleted && (
            <motion.button
              onClick={handleFinish}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all"
              title="Complete task (Enter)"
            >
              <FiCheck className="w-5 h-5" />
            </motion.button>
          )}
          
          {/* Toggle status */}
          <button
            onClick={handleToggle}
            className={`p-2.5 rounded-xl transition-all ${
              isCompleted 
                ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25' 
                : isInProgress
                ? 'bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25'
                : 'bg-gray-800/80 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            title="Cycle status"
          >
            {isCompleted ? <FiCheck className="w-4 h-4" /> : 
             isInProgress ? <FiPlay className="w-4 h-4" /> : <FiClock className="w-4 h-4" />}
          </button>
          
          {/* Edit */}
          <button
            onClick={handleEdit}
            className="p-2.5 rounded-xl bg-gray-800/80 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
            title="Edit task (E)"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          
          {/* Delete */}
          <button
            onClick={handleDelete}
            className="p-2.5 rounded-xl bg-gray-800/80 text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
            title="Delete task"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
});

export default SortableTaskItem;
